import React, { useMemo } from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  DEFAULT_SCHEDULE_BUSINESS_HOURS,
  SCHEDULE_RUN_WINDOW_MODES,
  SCHEDULE_TIMEZONE_OPTIONS,
  SCHEDULE_WEEKDAYS,
  formatScheduleRunWindowLabel,
  normalizeScheduleRunWindow,
  toTimeInputValue,
} from "../utils/scheduleRunWindow";

/**
 * Seleção de janela de execução: 24h, horário comercial ou horário definido.
 */
export default function ScheduleRunWindowField({
  value,
  onChange,
  onLabel,
  disabled = false,
}) {
  const window = useMemo(() => normalizeScheduleRunWindow(value), [value]);

  const emit = (next) => {
    const normalized = normalizeScheduleRunWindow(next);
    onChange?.(normalized);
    onLabel?.(formatScheduleRunWindowLabel(normalized));
  };

  const setMode = (mode) => {
    if (mode === "24h") {
      emit({ mode: "24h" });
      return;
    }
    if (mode === "business_hours") {
      emit({
        mode: "business_hours",
        timezone: window.timezone || DEFAULT_SCHEDULE_BUSINESS_HOURS.timezone,
      });
      return;
    }
    emit({
      mode: "defined_schedule",
      timezone: window.timezone || DEFAULT_SCHEDULE_BUSINESS_HOURS.timezone,
      weekdays: window.weekdays?.length
        ? window.weekdays
        : [...DEFAULT_SCHEDULE_BUSINESS_HOURS.weekdays],
      start: window.start || DEFAULT_SCHEDULE_BUSINESS_HOURS.start,
      end: window.end || DEFAULT_SCHEDULE_BUSINESS_HOURS.end,
    });
  };

  const toggleWeekday = (day) => {
    const set = new Set(window.weekdays || []);
    if (set.has(day)) set.delete(day);
    else set.add(day);
    emit({
      ...window,
      mode: "defined_schedule",
      weekdays: [...set].sort((a, b) => a - b),
    });
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Quando a regra pode rodar
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Define em quais horários o timer pode disparar esta automação. Fora da
        janela, a regra aguarda a próxima abertura.
      </Typography>

      <FormControl component="fieldset" disabled={disabled} fullWidth>
        <RadioGroup
          value={window.mode}
          onChange={(e) => setMode(e.target.value)}
        >
          {SCHEDULE_RUN_WINDOW_MODES.map((opt) => (
            <FormControlLabel
              key={opt.value}
              value={opt.value}
              control={<Radio size="small" />}
              label={
                <Box sx={{ py: 0.25 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {opt.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {opt.helper}
                  </Typography>
                </Box>
              }
              sx={{ alignItems: "flex-start", mb: 0.5 }}
            />
          ))}
        </RadioGroup>
      </FormControl>

      {window.mode !== "24h" && (
        <Stack spacing={2} sx={{ mt: 1.5, pl: { xs: 0, sm: 4 } }}>
          <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel>Fuso horário</InputLabel>
            <Select
              label="Fuso horário"
              value={window.timezone || DEFAULT_SCHEDULE_BUSINESS_HOURS.timezone}
              onChange={(e) =>
                emit({ ...window, timezone: e.target.value })
              }
            >
              {SCHEDULE_TIMEZONE_OPTIONS.map((tz) => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              O horário comercial e o horário definido usam este fuso.
            </FormHelperText>
          </FormControl>

          {window.mode === "defined_schedule" && (
            <>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.75 }}
                >
                  Dias da semana
                </Typography>
                <FormGroup row>
                  {SCHEDULE_WEEKDAYS.map(({ value: day, label }) => (
                    <FormControlLabel
                      key={day}
                      control={
                        <Checkbox
                          size="small"
                          checked={(window.weekdays || []).includes(day)}
                          onChange={() => toggleWeekday(day)}
                          disabled={disabled}
                        />
                      }
                      label={label}
                    />
                  ))}
                </FormGroup>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Início"
                  type="time"
                  value={toTimeInputValue(window.start)}
                  onChange={(e) =>
                    emit({ ...window, mode: "defined_schedule", start: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  disabled={disabled}
                />
                <TextField
                  label="Fim"
                  type="time"
                  value={toTimeInputValue(window.end)}
                  onChange={(e) =>
                    emit({ ...window, mode: "defined_schedule", end: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  disabled={disabled}
                />
              </Stack>
            </>
          )}
        </Stack>
      )}
    </Box>
  );
}
